import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Coins, TrendingUp, Crown, PenTool, Plus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSeo from "../lib/useSeo";

export default function WriterDashboardPage() {
    useSeo({
        title: "Writer Dashboard",
        description:
            "Your EIGOO Books writer dashboard — titles, sales, and earnings at your royalty split.",
    });
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/writer/dashboard").then(({ data }) => setData(data));
    }, []);

    if (!data)
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="eyebrow text-[#D4AF37] animate-pulse">
                    Loading your imprint…
                </div>
            </div>
        );

    const splitPct = Math.round((data.split_pct || 0) * 100);
    const tierLabel = data.tier.charAt(0).toUpperCase() + data.tier.slice(1);

    const tiles = [
        {
            icon: BookOpen,
            label: "Published titles",
            value: data.total_titles,
            sub: "Across the catalog",
        },
        {
            icon: TrendingUp,
            label: "Total sales",
            value: data.total_sales,
            sub: `Gross $${data.gross_revenue.toFixed(2)}`,
        },
        {
            icon: Coins,
            label: "Your earnings",
            value: `$${data.earnings.toFixed(2)}`,
            sub: `${splitPct}% royalty on every sale`,
        },
    ];

    return (
        <main
            data-testid="writer-dashboard-page"
            className="max-w-7xl mx-auto px-6 lg:px-10 py-20"
        >
            <div className="mb-14 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-3 flex items-center gap-2">
                        <PenTool className="w-3.5 h-3.5" /> Writer dashboard
                    </div>
                    <h1 className="font-serif text-5xl md:text-6xl tracking-tight">
                        Welcome,{" "}
                        <span className="text-gold-gradient italic">{user?.name}</span>
                    </h1>
                    <p className="text-[#A1A1AA] mt-4 max-w-xl">
                        Your imprint at EIGOO Books — titles, sales, and royalties in one place.
                    </p>
                </div>

                <div className="frame-gold px-6 py-4 flex items-center gap-4">
                    <Crown className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                        <div className="eyebrow text-[10px]">Your tier</div>
                        <div className="font-serif text-xl">
                            {tierLabel}{" "}
                            <span className="text-[#A1A1AA] text-sm">· {splitPct}% split</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                {tiles.map((t, i) => (
                    <motion.div
                        key={t.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        data-testid={`writer-stat-${t.label.toLowerCase().replace(/ /g, "-")}`}
                        className="frame-gold p-8"
                    >
                        <t.icon className="w-6 h-6 text-[#D4AF37] mb-6" />
                        <div className="font-serif text-5xl text-gold-gradient">{t.value}</div>
                        <div className="eyebrow mt-3 text-[10px]">{t.label}</div>
                        <div className="text-[#A1A1AA] text-sm mt-1">{t.sub}</div>
                    </motion.div>
                ))}
            </div>

            <div className="mb-6 flex items-end justify-between">
                <div>
                    <div className="eyebrow mb-2">Your titles</div>
                    <h2 className="font-serif text-3xl">Catalog</h2>
                </div>
                <button
                    data-testid="writer-new-title-btn"
                    disabled
                    title="Manuscript submission UI arrives in the next release"
                    className="btn-ghost-gold !py-2 !px-4 text-[10px] opacity-60 cursor-not-allowed"
                >
                    <Plus className="w-3 h-3" /> New title (soon)
                </button>
            </div>

            {data.titles.length === 0 ? (
                <div className="frame-gold p-12 text-center" data-testid="writer-empty">
                    <Crown className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
                    <p className="font-serif italic text-xl text-[#E6D59A]">
                        No titles yet — but your shelf is ready.
                    </p>
                    <p className="text-[#A1A1AA] text-sm mt-3 max-w-md mx-auto">
                        Submit your first manuscript to the editorial team, and we'll handle
                        the production, listing, and promotion.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {data.titles.map((t) => (
                        <div
                            key={t.id}
                            data-testid={`writer-title-${t.id}`}
                            className="frame-gold p-5 flex items-center gap-5"
                        >
                            <div
                                className="w-14 h-20 bg-cover bg-center flex-shrink-0"
                                style={{ backgroundImage: `url(${t.cover_url})` }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-serif text-xl text-white">{t.title}</div>
                                <div className="text-xs text-[#A1A1AA] italic truncate">
                                    {t.synopsis}
                                </div>
                                <div className="eyebrow text-[10px] mt-2">
                                    {t.status} · ${t.price?.toFixed?.(2) || "0.00"} ·{" "}
                                    {Math.round((t.writer_split_pct || 0) * 100)}% to you
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </main>
    );
}
